import React, { useState } from 'react';
import { Megaphone, Lock, Save, Eye, EyeOff, BarChart3 } from 'lucide-react';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_IMPACT = {
  title: 'Il Nostro Impegno per il Terzo Settore',
  subtitle: 'Numeri che raccontano il nostro impatto quotidiano nel supportare chi opera per il bene comune.',
  date_note: 'Dal 21.02.2024 ad oggi',
  counters: [
    { id: 'rca', value: 409, label: "Richieste RCA 'Basta la Targa' gestite con successo" },
    { id: 'quotazioni', value: 283, label: 'Schede di Quotazione e Analisi Rischi per Cooperative e Enti No-Profit' },
    { id: 'consulenze', value: 44, label: 'Consulenze specialistiche prenotate online' },
    { id: 'ore_gratuite', value: 160, label: 'Ore di Consulenza Gratuita donate agli Enti del Terzo Settore' },
  ],
};

const COUNTER_NAMES = {
  rca: 'Contatore 1 — RCA',
  quotazioni: 'Contatore 2 — Quotazioni',
  consulenze: 'Contatore 3 — Consulenze',
  ore_gratuite: 'Contatore 4 — Ore gratuite (Società Benefit)',
};

export default function AdminAvvisi() {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [active, setActive] = useState(true);
  const [impact, setImpact] = useState(DEFAULT_IMPACT);
  const [savingImpact, setSavingImpact] = useState(false);

  const loadImpact = async () => {
    try {
      const res = await fetch(`${API}/api/impact`);
      const data = await res.json();
      if (data.impact) {
        setImpact({
          title: data.impact.title || DEFAULT_IMPACT.title,
          subtitle: data.impact.subtitle || DEFAULT_IMPACT.subtitle,
          date_note: data.impact.date_note || DEFAULT_IMPACT.date_note,
          counters: DEFAULT_IMPACT.counters.map((base) => {
            const saved = (data.impact.counters || []).find((c) => c.id === base.id);
            return saved ? { ...base, value: saved.value, label: saved.label } : base;
          }),
        });
      }
    } catch {}
  };

  const updateCounter = (id, field, value) => {
    setImpact((prev) => ({
      ...prev,
      counters: prev.counters.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    }));
  };

  const saveImpact = async (e) => {
    e.preventDefault();
    setSavingImpact(true);
    try {
      const res = await fetch(`${API}/api/admin/impact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          title: impact.title.trim(),
          subtitle: impact.subtitle.trim(),
          date_note: impact.date_note.trim(),
          counters: impact.counters.map((c) => ({ ...c, value: parseInt(c.value, 10) || 0 })),
        }),
      });
      if (res.ok) {
        toast.success('Statistiche impatto salvate con successo');
      } else {
        toast.error('Errore durante il salvataggio');
      }
    } catch {
      toast.error('Errore di connessione. Riprova.');
    } finally {
      setSavingImpact(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/announcement/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.status === 401) {
        toast.error('Password non valida');
        return;
      }
      const data = await res.json();
      if (data.announcement) {
        setText(data.announcement.text || '');
        setExpiresAt((data.announcement.expires_at || '').slice(0, 10));
        setActive(data.announcement.active !== false);
      }
      setAuthed(true);
    } catch {
      toast.error('Errore di connessione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      toast.error('Inserisci il testo dell\'avviso');
      return;
    }
    if (!expiresAt) {
      toast.error('Imposta la data di scadenza');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/announcement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, text: text.trim(), expires_at: expiresAt, active }),
      });
      if (res.ok) {
        toast.success('Avviso salvato con successo');
      } else {
        toast.error('Errore durante il salvataggio');
      }
    } catch {
      toast.error('Errore di connessione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B7A9F] to-[#3AAFA9] flex items-center justify-center px-4 py-12">
      <Toaster position="top-right" richColors />
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#2B7A9F]/10 flex items-center justify-center">
            <Megaphone size={22} className="text-[#2B7A9F]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Comunicazioni di servizio</h1>
            <p className="text-sm text-gray-500">Area riservata ADMIN</p>
          </div>
        </div>

        {!authed ? (
          <form onSubmit={login} className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Password ADMIN</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="admin-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F]"
                placeholder="Inserisci la password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Mostra password"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#2B7A9F] hover:bg-[#256b8c] text-white font-semibold py-2.5 rounded-lg transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? 'Verifica...' : 'Accedi'}
            </button>
          </form>
        ) : (
          <form onSubmit={save} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Testo dell'avviso</label>
              <textarea
                data-testid="announcement-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                maxLength={1000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F] resize-y"
                placeholder="Es. Chiusura straordinaria per formazione il giorno..."
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{text.length}/1000</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Data di scadenza <span className="text-gray-400 font-normal">(l'avviso sparirà automaticamente da questa data)</span>
              </label>
              <input
                data-testid="announcement-expiry-input"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F]"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="announcement-active-checkbox"
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="w-4 h-4 accent-[#2B7A9F]"
              />
              <span className="text-sm text-gray-700">Avviso attivo (visibile sul sito)</span>
            </label>

            {text.trim() && active && (
              <div className="rounded-xl bg-gradient-to-r from-[#2B7A9F] to-[#3AAFA9] p-4">
                <p className="text-xs text-white/70 mb-2 uppercase tracking-wide">Anteprima</p>
                <div className="flex items-start gap-2 bg-white/15 border border-[#F9D424]/40 rounded-lg px-3 py-2">
                  <Megaphone size={15} className="text-[#F9D424] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-white leading-relaxed">
                    <span className="font-semibold text-[#F9D424]">Avviso: </span>
                    {text}
                  </p>
                </div>
              </div>
            )}

            <button
              data-testid="announcement-save-btn"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#2B7A9F] hover:bg-[#256b8c] text-white font-semibold py-2.5 rounded-lg transition-colors duration-300 disabled:opacity-60"
            >
              <Save size={16} />
              {loading ? 'Salvataggio...' : 'Salva avviso'}
            </button>
          </form>
        )}

        {authed && (
          <form onSubmit={saveImpact} className="mt-10 pt-8 border-t border-gray-200 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#5FB76E]/10 flex items-center justify-center">
                <BarChart3 size={22} className="text-[#5FB76E]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">Statistiche Impatto</h2>
                <p className="text-sm text-gray-500">Sezione "Il Nostro Impegno per il Terzo Settore" in homepage</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Titolo</label>
              <input
                data-testid="impact-title-input"
                type="text"
                value={impact.title}
                onChange={(e) => setImpact((p) => ({ ...p, title: e.target.value }))}
                maxLength={200}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Sottotitolo</label>
              <textarea
                data-testid="impact-subtitle-input"
                value={impact.subtitle}
                onChange={(e) => setImpact((p) => ({ ...p, subtitle: e.target.value }))}
                rows={2}
                maxLength={500}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F] resize-y"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nota data (es. "Dal 21.02.2024 ad oggi")</label>
              <input
                data-testid="impact-date-input"
                type="text"
                value={impact.date_note}
                onChange={(e) => setImpact((p) => ({ ...p, date_note: e.target.value }))}
                maxLength={100}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F]"
              />
            </div>

            <div className="space-y-4">
              {impact.counters.map((c) => (
                <div key={c.id} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{COUNTER_NAMES[c.id]}</p>
                  <div className="flex gap-3">
                    <div className="w-28 flex-shrink-0">
                      <label className="block text-xs text-gray-500 mb-1">Numero</label>
                      <input
                        data-testid={`impact-value-${c.id}`}
                        type="number"
                        min="0"
                        value={c.value}
                        onChange={(e) => updateCounter(c.id, 'value', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F]"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Descrizione</label>
                      <input
                        data-testid={`impact-label-${c.id}`}
                        type="text"
                        value={c.label}
                        maxLength={300}
                        onChange={(e) => updateCounter(c.id, 'label', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              data-testid="impact-save-btn"
              type="submit"
              disabled={savingImpact}
              className="w-full flex items-center justify-center gap-2 bg-[#5FB76E] hover:bg-[#4fa35e] text-white font-semibold py-2.5 rounded-lg transition-colors duration-300 disabled:opacity-60"
            >
              <Save size={16} />
              {savingImpact ? 'Salvataggio...' : 'Salva statistiche'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
